import { type ApiProblem, type ApiSchemas, call } from '@rental/contracts/client'
import {
  DEPOSIT_STATES,
  type DepositState,
  REFUND_STATES,
  type RefundState,
  type StaffLocale,
} from '@rental/domain'
import { format, formatDate, formatDateTime, formatEuro, type StaffMessages } from '@rental/i18n'
import { Button, cn, Field } from '@rental/ui'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { getRouteApi, Link } from '@tanstack/react-router'
import { type FormEvent, useId, useState } from 'react'
import { can } from '../access'
import { useAccess } from '../access-context'
import { api, load } from '../api'
import { addDays } from '../board'
import { DateStepper } from '../components/DateStepper'
import { OutcomeNotice } from '../components/OutcomeNotice'
import { PageHeader } from '../components/PageHeader'
import { PendingLine } from '../components/PendingBadge'
import { Fetching, ROW_STILL } from '../components/Results'
import { Section } from '../components/Section'
import { StatusChip } from '../components/StatusChip'
import { ReasonList } from '../components/SubPanel'
import { Guard, LoadState } from '../components/states'
import { TechnicalLines } from '../components/TechnicalDetails'
import { Facts, ProblemNotice, StaffTextarea } from '../components/ui'
import { useLocale } from '../i18n'
import { problemOf } from '../problems'
import { BookingCode } from '../reports/BookingCode'
import { useRomeToday } from '../rome-today'

const route = getRouteApi('/reconciliation')

type Reconciliation = ApiSchemas['StaffReconciliation']
type Run = NonNullable<Reconciliation['run']>
type Difference = ApiSchemas['StaffDifference']

const QUERY_KEY = 'reconciliation'

/**
 * S5c: the night's check against Stripe, for the holders of reports.view.
 * A day's latest check (Stripe's totals, what it recorded that Stripe had
 * not managed to tell the app, the money taken outside the app), the
 * differences still to read, oldest first, each read once with a reason, and
 * those found on the day. There is no button to check again (cut, plan S5c).
 * Without Stripe's keys, the screen says so. UI-2e: everything is said in
 * words; Stripe's and the app's own codes only under "Dettagli tecnici", for
 * whoever looks after the system. Stripe's reference for the movement stays in
 * sight, named (UI-2g): it is how the movement is found in Stripe's Dashboard,
 * which is what the screen asks its reader to check.
 */
export function ReconciliationScreen() {
  const { locale, messages: m } = useLocale()
  const { access } = useAccess()
  const allowed = can(access, 'reports.view')
  const search = route.useSearch()
  const navigate = route.useNavigate()
  // The last day a night has checked: what the screen opens on, and where "Ieri" leads back.
  // It follows the clock, and so does the read (UI-2g): a screen left open past midnight
  // reads the new last day, and "Ieri" leads there, not to the day before it.
  const yesterday = addDays(useRomeToday(), -1)
  const view = useQuery({
    queryKey: [QUERY_KEY, search.date ?? null, yesterday],
    queryFn: () =>
      load(
        call(
          api.GET('/v1/staff/reconciliation', {
            params: { query: search.date ? { date: search.date } : {} },
          }),
        ),
      ),
    enabled: allowed,
    placeholderData: keepPreviousData,
  })
  const data = view.data
  const r = m.reconcile
  const shown = search.date ?? data?.date ?? yesterday

  return (
    <Guard allowed={allowed} title={r.title}>
      <PageHeader title={r.title} intro={r.intro}>
        <div className="flex flex-col gap-4 pt-2">
          {data?.stripe === 'pending' ? <PendingLine>{r.pending}</PendingLine> : null}
          <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
            <DateStepper
              label={r.date}
              value={shown}
              today={yesterday}
              step={1}
              words={{ earlier: r.dayBefore, later: r.dayAfter, today: r.yesterday }}
              onChange={(date) =>
                void navigate({
                  to: '/reconciliation',
                  search: { date: date === yesterday ? undefined : date },
                })
              }
            />
            {view.isPlaceholderData ? <Fetching /> : null}
          </div>
        </div>
      </PageHeader>
      <LoadState data={data} problem={problemOf(view.error)}>
        {(data) => (
          <>
            {/* Without keys and without a check, the line above says it all: no empty pane. */}
            {data.run || data.stripe === 'configured' ? (
              <Section title={formatDate(locale, data.date)}>
                {data.run ? (
                  <RunFacts run={data.run} runs={data.runs} />
                ) : (
                  <NoRun date={data.date} dueAt={data.dueAt} />
                )}
              </Section>
            ) : null}
            <OpenDifferences data={data} />
            <div data-testid="differences-of-date">
              <Section title={r.ofDate}>
                {data.ofDate.length === 0 ? (
                  <p className="text-text-muted">{r.noneOfDate}</p>
                ) : (
                  <ul className="flex flex-col">
                    {data.ofDate.map((difference) => (
                      <li key={difference.id} className={cn(ROW_STILL, 'gap-2')}>
                        <DifferenceLine difference={difference} />
                        <DifferenceFate difference={difference} />
                      </li>
                    ))}
                  </ul>
                )}
                {data.ofDateCount > data.ofDate.length ? (
                  <p className="text-sm text-text-muted">
                    {format(r.more, { count: data.ofDateCount - data.ofDate.length })}
                  </p>
                ) : null}
              </Section>
            </div>
          </>
        )}
      </LoadState>
    </Guard>
  )
}

/** No check of the day yet: when it is due, or, past that, that it did not run and where to look. */
function NoRun({ date, dueAt }: { date: string; dueAt: string }) {
  const { locale, messages: m } = useLocale()
  const { access } = useAccess()
  const r = m.reconcile
  if (Date.now() < new Date(dueAt).getTime()) {
    return (
      <p>
        {format(r.due, { date: formatDate(locale, date), when: formatDateTime(locale, dueAt) })}
      </p>
    )
  }
  return (
    <p>
      {format(r.notRun, { date: formatDate(locale, date) })}
      {can(access, 'system.view') ? (
        <>
          {' '}
          <Link to="/system" className="touch-target">
            {m.nav.system}
          </Link>
        </>
      ) : null}
    </p>
  )
}

/** A day's latest check: when and how it ran, Stripe's totals, what it recorded, the money outside. */
function RunFacts({ run, runs }: { run: Run; runs: number }) {
  const { locale, messages: m } = useLocale()
  const r = m.reconcile
  const euro = (cents: number) => <span className="figures">{formatEuro(cents, locale)}</span>
  const outcomes: Readonly<Record<string, string>> = r.outcomes
  const categories: Readonly<Record<string, string>> = r.categories
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <p>
          {format(r.run, {
            date: formatDate(locale, run.businessDate),
            when: formatDateTime(locale, run.finishedAt),
            trigger: r.triggers[run.trigger],
          })}
        </p>
        {run.livemode ? null : <StatusChip tone="info">{r.testMode}</StatusChip>}
      </div>
      {runs > 1 ? (
        <p className="text-sm text-text-muted">{format(r.runs, { count: runs })}</p>
      ) : null}
      <Facts
        items={[
          [
            r.totals.transactions,
            <span key="count" className="figures">
              {run.transactions}
            </span>,
          ],
          [r.totals.charges, euro(run.chargesCents)],
          [r.totals.refunds, euro(run.refundsCents)],
          [r.totals.disputes, euro(run.disputesCents)],
          [r.totals.fees, euro(run.feesCents)],
          [r.totals.payouts, euro(run.payoutsCents)],
          [r.totals.other, euro(run.otherCents)],
          [r.totals.net, euro(run.netCents)],
          [r.totals.outside, euro(run.outsideCents)],
        ]}
      />
      <div className="flex flex-col gap-2">
        <h3 className="text-lg">{r.repairs}</h3>
        {run.repairs.length === 0 ? (
          <p className="text-text-muted">{r.noRepairs}</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {run.repairs.map((repair) => (
              <li key={`${repair.kind}:${repair.stripeId}`} className="flex flex-wrap gap-x-3">
                <span>
                  {`${r.repairKinds[repair.kind]} ${outcomes[repair.outcome] ?? r.outcomeUnknown}`}
                </span>
                <BookingCode code={repair.code} />
                <span className="figures text-sm text-text-muted">{repair.stripeId}</span>
              </li>
            ))}
          </ul>
        )}
        <TechnicalLines
          lines={run.repairs
            .filter((repair) => outcomes[repair.outcome] === undefined)
            .map((repair) => `${repair.stripeId}: ${repair.outcome}`)}
        />
      </div>
      <div className="flex flex-col gap-2">
        <h3 className="text-lg">{r.outside}</h3>
        <p className="text-sm text-text-muted">{r.outsideHint}</p>
        {run.outside.length === 0 ? (
          <p className="text-text-muted">{r.noOutside}</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {run.outside.map((money) => (
              <li key={money.stripeId} className="flex flex-wrap gap-x-3">
                <span>{categories[money.category] ?? r.categoryUnknown}</span>
                <span className="figures">{formatEuro(money.amountCents, locale)}</span>
                <span className="figures text-sm text-text-muted">{money.stripeId}</span>
                <span className="text-sm text-text-muted">{formatDateTime(locale, money.at)}</span>
              </li>
            ))}
          </ul>
        )}
        <TechnicalLines
          lines={run.outside
            .filter((money) => categories[money.category] === undefined)
            .map(
              (money) => `${money.stripeId}: ${format(m.app.stripeCode, { code: money.category })}`,
            )}
        />
      </div>
    </>
  )
}

const isRefundState = (value: unknown): value is RefundState =>
  typeof value === 'string' && (REFUND_STATES as readonly string[]).includes(value)
const isDepositState = (value: unknown): value is DepositState =>
  typeof value === 'string' && (DEPOSIT_STATES as readonly string[]).includes(value)

/**
 * What a difference's details say, in words, for whoever reads the check:
 * Stripe's kind of movement, the amounts the app holds, the statuses on
 * either side. What has no words (ids, the app's own outcome codes) is only
 * under "Dettagli tecnici".
 */
export function detailWords(
  details: Difference['details'],
  locale: StaffLocale,
  m: StaffMessages,
): string[] {
  const r = m.reconcile
  const said = r.detail
  const categories: Readonly<Record<string, string>> = r.categories
  const euro = (value: unknown) => (typeof value === 'number' ? formatEuro(value, locale) : null)
  const refund = (value: unknown) => (isRefundState(value) ? m.refund.states[value] : null)
  const lines: string[] = []
  if (typeof details.category === 'string') {
    lines.push(
      format(said.category, { category: categories[details.category] ?? r.categoryUnknown }),
    )
  }
  const app = euro(details.appCents)
  if (app) lines.push(format(said.appAmount, { amount: app }))
  const stripeKept = euro(details.stripeKeptCents)
  const appKept = euro(details.appKeptCents)
  if (stripeKept && appKept) lines.push(format(said.kept, { stripe: stripeKept, app: appKept }))
  if (isDepositState(details.depositState)) {
    lines.push(format(said.depositState, { state: r.depositStates[details.depositState] }))
  }
  const appStatus = refund(details.appStatus)
  const stripeStatus = refund(details.stripeStatus)
  if (appStatus && stripeStatus) {
    lines.push(format(said.refundStatus, { app: appStatus, stripe: stripeStatus }))
  }
  const status = refund(details.status)
  if (status) lines.push(format(said.stripeRefund, { status }))
  return lines
}

/**
 * A difference's amount. Stripe counts in the currency's smallest unit: only
 * euro cents are the app's to format.
 */
function differenceAmount(difference: Difference, locale: StaffLocale, m: StaffMessages): string {
  return difference.currency === 'eur'
    ? formatEuro(difference.amountCents, locale)
    : format(m.reconcile.minorUnits, {
        amount: difference.amountCents,
        currency: difference.currency.toUpperCase(),
      })
}

/**
 * A difference's name, said by its form, field and button (UI-2g): with a form per
 * difference, every field and button had the next one's name. Its kind, amount and booking.
 */
function differenceName(difference: Difference, locale: StaffLocale, m: StaffMessages): string {
  const parts = [m.reconcile.kinds[difference.kind], differenceAmount(difference, locale, m)]
  return (difference.code === null ? parts : [...parts, difference.code]).join(', ')
}

/** What a difference is, in words, with its amount, booking and Stripe's id. */
function DifferenceLine({ difference }: { difference: Difference }) {
  const { locale, messages: m } = useLocale()
  const r = m.reconcile
  const amount = differenceAmount(difference, locale, m)
  const words = detailWords(difference.details, locale, m)
  return (
    <div className="flex flex-col gap-1">
      <p className="first-letter:uppercase">{r.kinds[difference.kind]}</p>
      <p className="flex flex-wrap gap-x-3 text-sm">
        <span className="figures font-medium">{amount}</span>
        <BookingCode code={difference.code} />
        <span className="text-text-muted">
          {format(r.stripeReference, { id: difference.stripeId })}
        </span>
        <span className="text-text-muted">
          {format(r.since, { date: formatDate(locale, difference.businessDate, 'short') })}
        </span>
      </p>
      {words.length > 0 ? (
        <ul className="flex flex-col gap-1 text-sm">
          {words.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      ) : null}
      <TechnicalLines
        lines={Object.entries(difference.details).map(([key, value]) => `${key}: ${String(value)}`)}
      />
    </div>
  )
}

/** What became of a difference: still found, gone from a later check, or read. */
function DifferenceFate({ difference }: { difference: Difference }) {
  const { locale, messages: m } = useLocale()
  const r = m.reconcile
  if (difference.acknowledgedAt) {
    return (
      <p className="text-sm text-text-muted">
        {format(r.acknowledged, {
          name: difference.acknowledgedBy ?? '',
          when: formatDateTime(locale, difference.acknowledgedAt),
          reason: difference.acknowledgement ?? '',
        })}
      </p>
    )
  }
  if (difference.resolvedAt) {
    return (
      <p className="text-sm text-text-muted">
        {format(r.resolved, { when: formatDateTime(locale, difference.resolvedAt) })}
      </p>
    )
  }
  return null
}

/**
 * The differences no one has read yet, oldest first, each with its
 * acknowledgement form. The one read leaves the list with its form: what it
 * did is said at the top, and takes the focus.
 */
function OpenDifferences({ data }: { data: Reconciliation }) {
  const { messages: m } = useLocale()
  const r = m.reconcile
  const [done, setDone] = useState<number | null>(null)
  return (
    <div data-testid="differences-open">
      <Section title={r.open}>
        <p className="text-sm text-text-muted">{r.openHint}</p>
        {done === null ? null : (
          <OutcomeNotice key={done}>
            <p>{r.acknowledgedDone}</p>
          </OutcomeNotice>
        )}
        {data.open.length === 0 ? (
          <p className="text-text-muted">{r.noOpen}</p>
        ) : (
          <ul className="flex flex-col">
            {data.open.map((difference) => (
              <li key={difference.id} className={cn(ROW_STILL, 'gap-3')}>
                <DifferenceLine difference={difference} />
                <AcknowledgeForm difference={difference} onDone={() => setDone(Date.now())} />
              </li>
            ))}
          </ul>
        )}
        {data.openCount > data.open.length ? (
          <p className="text-sm text-text-muted">
            {format(r.more, { count: data.openCount - data.open.length })}
          </p>
        ) : null}
      </Section>
    </div>
  )
}

/** Marks a difference read, once, with what was checked or done: it stays in the log. */
function AcknowledgeForm({ difference, onDone }: { difference: Difference; onDone: () => void }) {
  const { locale, messages: m } = useLocale()
  const name = differenceName(difference, locale, m)
  const { takeOver } = useAccess()
  const queryClient = useQueryClient()
  const r = m.reconcile
  const reasonsId = useId()
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  const trimmed = reason.trim()
  const ready = trimmed.length >= 3 && trimmed.length <= 500
  const reasons = ready || busy ? [] : [r.why]

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!ready || busy) return
    setProblem(null)
    setBusy(true)
    const result = await call(
      api.POST('/v1/staff/reconciliation/differences/{differenceId}/acknowledge', {
        params: { path: { differenceId: difference.id } },
        body: { reason: trimmed },
      }),
    )
    setBusy(false)
    if (result.ok) onDone()
    // Read meanwhile on another device, or read here: either way the list is as it is now.
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    if (!result.ok && !takeOver(result.problem)) setProblem(result.problem)
  }

  return (
    <form
      aria-label={`${r.acknowledge}: ${name}`}
      className="flex flex-col gap-3"
      onSubmit={submit}
      noValidate
    >
      <Field
        label={
          <>
            {r.reason}
            <span className="sr-only">, {name}</span>
          </>
        }
        hint={r.reasonHint}
        required
        error={trimmed.length > 0 && trimmed.length < 3 ? m.refund.reasonShort : null}
      >
        {(control) => (
          <StaffTextarea
            {...control}
            rows={2}
            maxLength={500}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        )}
      </Field>
      <ProblemNotice problem={problem} />
      <div>
        <Button
          type="submit"
          variant="staff"
          busy={busy}
          disabled={reasons.length > 0}
          aria-describedby={reasons.length > 0 ? reasonsId : undefined}
        >
          {r.acknowledge}
          <span className="sr-only">: {name}</span>
        </Button>
      </div>
      <ReasonList id={reasonsId} reasons={reasons} />
    </form>
  )
}
